import React, { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getStatusColor, getStatusText, getRiskStatusBadge, getRiskText, getRiskColor, getRiskTextColor, formatSymptomLevel, formatApiDate, calculateAge } from "../../../../utils/index";
import { getAppointmentCounter, getAppointmentDetailData } from "../../api/Appointments/AppointmentDetail";


// Header component with logo and branding
const PDFHeader = () => {
  return (
    <div className="relative w-full h-[45px] mb-20 pdf-header-running">
      <div className="absolute bg-[#0b4f9f] h-[20.748px] left-0 top-0 w-full" />
      
      <div className="absolute h-[44.46px] left-0 top-[0.74px] w-[419.403px]">
        <img 
          alt="PhyberCare Logo" 
          src={require('../../../../assets/images/pdf_header.svg').default}
        />
      </div>
    </div>
  );
};

// Footer component
const PDFFooter = () => {
  return (
    <div className="relative w-full mt-12 pb-8 pdf-footer-running">
      {/* Footer Content Section */}
      <div className="relative z-10">
        <div className="flex justify-between items-center">
          {/* Left Side - Logo and "Prepared using phyber" */}
          <div className="flex flex-col items-start justify-start">
            {/* Logo */}
            <div className="h-[29.827px] w-[105px] mb-2">
              <img 
                alt="Phyber Report Logo" 
                src={require('../../../../assets/images/pyhber_report_logo.png')}
                className="w-full h-full object-contain"
              />
            </div>
            
            {/* "Prepared using phyber" text */}
            <div className="font-poppins font-normal leading-normal text-[#4B4B4B] text-[14px] tracking-[0.28px]">
              <p>Prepared using phyber</p>
            </div>
          </div>

          {/* Right Side - Disclaimer Text */}
          <div className="flex-1 text-center">
            <p className="font-poppins font-normal text-[#334155] text-[16px] leading-relaxed">
              This report contains sensitive health information and is intended only for authorized personnel.
            </p>
          </div>
        </div>
      </div>

      {/* Blue footer bar - positioned at the very bottom */}
      <div className="absolute bg-[#0b4f9f] h-[20.748px] left-0 bottom-0 w-full" />
      
      {/* Logo/Branding - positioned on the blue bar */}
      <div className="absolute h-[44.46px] right-0 bottom-[0.74px] w-[419.403px]">
          <img 
            alt="PhyberCare Logo" 
            src={require('../../../../assets/images/pdf_footer.svg').default}
          />
      </div>
    </div>
  );
};

// Risk status badge component - using utility function for consistency
const RiskStatusBadge = ({ risk, session }) => {
  // Check if this should be N/A based on original session data
  let effectiveRisk = risk;
  if (session && session.riskStatus === null && risk === "NO_RISK") {
    effectiveRisk = null;
  }

  const badge = getRiskStatusBadge(effectiveRisk);

  return (
    <div
      className="px-3 py-0.5 rounded-[16px] flex items-center justify-center"
      style={{
        backgroundColor: badge.backgroundColor,
        color: badge.color
      }}
    >
      <span className="font-poppins font-medium text-[14px] uppercase tracking-[0.5px]">
        {badge.text}
      </span>
    </div>
  );
};

// Status indicator with dot - using same logic as admin appointments
const StatusIndicator = ({ session }) => {
  // Use currentStatus if available, otherwise use main status
  let effectiveStatus = session.currentStatus || session.status;

  // Handle SCHEDULED appointments with date/time logic only if no currentStatus
  if (session.status === "SCHEDULED" && !session.currentStatus) {
    const appointmentDateTime = new Date(`${session.date}T${session.startTime || '00:00'}`);
    const now = new Date();

    if (appointmentDateTime < now) {
      effectiveStatus = "MISSED"; // Past scheduled appointment = missed
    } else {
      effectiveStatus = "SCHEDULED"; // Future scheduled appointment = upcoming
    }
  }

  // Handle specific currentStatus values
  if (session.currentStatus === "MISSED BY COUNSELLOR") {
    effectiveStatus = "MISSED";
  }

  const statusColor = getStatusColor(effectiveStatus);
  const statusText = getStatusText(effectiveStatus);

  return (
    <div className="flex gap-2 items-center">
      <div className={`w-3 h-3 ${statusColor} rounded-full`} />
      <span className="font-poppins font-semibold text-[#4B61FF] text-[16px] tracking-[0.32px]">
        {statusText}
      </span>
    </div>
  );
};

// Logo component for the PDF
const LogoSection = ({ organisationLogo }) => {
  return (
      <div className="relative h-20 mb-20">
        {organisationLogo && (
            <div className="absolute h-20 left-0 top-0 w-[200px]">
              <img
                  alt="Organisation Logo"
                  src={organisationLogo}
                  className="w-full h-full object-contain"
              />
            </div>
        )}
      </div>
  );
};

// PDF Download button component using native browser PDF
const PDFDownloadButton = ({ memberData }) => {
  const memberName = `${memberData?.memberName || 'Member'}`;

  const generatePDF = () => {
    // Set document title for PDF
    const originalTitle = document.title;
    document.title = `Member Summary Report - ${memberName}`;
    
    // Trigger browser's native print dialog which allows saving as PDF
    window.print();
    
    // Restore original title
    setTimeout(() => {
      document.title = originalTitle;
    }, 1000);
  };

  return (
    <div className="fixed top-4 right-4 z-50 print:hidden">
      <button 
        onClick={generatePDF}
        className="btn-gradient border-0 h-9 px-[14px] rounded-xl text-[13px] font-semibold font-poppins hover:opacity-95 transition flex items-center gap-2 shadow-lg"
        title="Download PDF Report"
      >
        <Download className="h-4 w-4" />
        Download PDF Report
      </button>
    </div>
  );
};

// Main PDF Page Component
export default function MemberSummaryReport() {
  const dispatch = useDispatch();
  const params = useParams();
  const [memberData, setMemberData] = useState(null);
  const [sessionsData, setSessionsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Redux state
  const { data: appointmentCounterData, loading: appointmentLoading } = useSelector(
    (state) => state.appointmentDetail?.appointmentCounter || {}
  );
  const { detail, loading: detailLoading } = useSelector(
    (state) => state.appointmentDetail?.appointmentDetail || {}
  );

  useEffect(() => {
    // Get appointment ID from URL parameters and UID from query string
    const { appointmentId } = params;
    const urlParams = new URLSearchParams(window.location.search);
    const uid = urlParams.get('uid');

    if (appointmentId) {
      // Fetch appointment data and detail data
      dispatch(getAppointmentCounter(JSON.stringify({})));
      dispatch(getAppointmentDetailData(appointmentId));
    } else {
      setIsLoading(false);
      // Set fallback data if no params
      setMemberData({
        memberName: "Sample Member",
        memberEmail: "sample@example.com",
        memberPhone: "+91-1234567890",
        memberGender: "Male",
        memberDOB: "1990-01-01",
        memberGroups: "Sample Group",
        createdOn: "2024-01-01",
        location: "Sample Location",
        emergencyContactName: "Sample Contact",
        emergencyContactRelationship: "Family",
        emergencyContactNumber: "+91-9876543210",
        organisationName: "Sample Organization"
      });
    }
  }, [dispatch, params]);

  useEffect(() => {
    // Process appointment data when available
    if (detail) {

      // Handle different possible response structures for detail
      let memberDataFromAPI = null;
      if (detail.payload) {
        memberDataFromAPI = detail.payload;
      } else if (detail.data) {
        memberDataFromAPI = detail.data;
      } else if (typeof detail === 'object' && detail.firstName) {
        memberDataFromAPI = detail;
      }

      if (memberDataFromAPI) {
        // Set member data from detail API
        setMemberData({
          memberName: `${memberDataFromAPI.firstName || ''} ${memberDataFromAPI.lastName || ''}`.trim(),
          memberEmail: memberDataFromAPI.email || 'N/A',
          memberPhone: memberDataFromAPI.mobile ? `${memberDataFromAPI.countryCode || '+91'}-${memberDataFromAPI.mobile}` : 'N/A',
          memberGender: memberDataFromAPI.gender || 'N/A',
          memberDOB: memberDataFromAPI.dateOfBirth || 'N/A',
          memberGroups: memberDataFromAPI.organisationName || 'N/A',
          createdOn: memberDataFromAPI.createdOn?.split(' ')[0] || 'N/A',
          location: memberDataFromAPI.location || 'N/A',
          emergencyContactName: memberDataFromAPI.emergencyContactName || 'N/A',
          emergencyContactRelationship: memberDataFromAPI.emergencyContactRelationship || 'N/A',
          emergencyContactNumber: memberDataFromAPI.emergencyContactNumber || 'N/A',
          organisationName: memberDataFromAPI.organisationName || 'N/A',
          organisationLogo: memberDataFromAPI.organisationLogo || null
        });

        if (appointmentCounterData) {
          let allAppointments = [];

          // Extract appointments from the data structure - use exact same logic as AppointmentDetails
          if (Array.isArray(appointmentCounterData)) {
            allAppointments = appointmentCounterData;
          } else if (typeof appointmentCounterData === 'object') {
            // Handle nested response structure - same as AppointmentDetails
            if (appointmentCounterData.payload) {
              allAppointments = Object.values(appointmentCounterData.payload).flat().filter(Boolean);
            } else if (appointmentCounterData.data) {
              allAppointments = Object.values(appointmentCounterData.data).flat().filter(Boolean);
            } else {
              // Try to extract from direct object values
              allAppointments = Object.values(appointmentCounterData).flat().filter(Boolean);
            }
          }


          // Filter to only get valid appointment objects (exclude strings like 'Success')
          allAppointments = allAppointments.filter(item =>
            item &&
            typeof item === 'object' &&
            item.appointmentId &&
            typeof item.appointmentId === 'string'
          );


          // Use the exact same logic as AppointmentDetails component
          const currentAppointmentId = params.appointmentId;
          let memberSessions = [];

          if (allAppointments.length > 0 && currentAppointmentId) {
            // Find the current appointment details (same as AppointmentDetails logic)
            let appointmentDetail = allAppointments.find(
              (item) => item.appointmentId === currentAppointmentId
            );

            if (appointmentDetail) {
              // Get all appointments for this user using the same logic as AppointmentDetails
              memberSessions = allAppointments.filter(
                (item) => item.uid === appointmentDetail.uid
              );

            }
          }

          // Process sessions
          let sessionsToProcess = memberSessions;

          // Process sessions data using exact same logic as AppointmentDetails
          const processedSessions = sessionsToProcess.map((session, index) => {
            // Process prescriptions by type
            const prescriptionsByType = (session.prescriptions || []).reduce((acc, prescription) => {
              acc[prescription.prescriptionType] = prescription;
              return acc;
            }, {});

            return {
              ...session,
              // Add practitioner name from counsellor fields
              practitioner: `${session.counsellorFirstName || ''} ${session.counsellorLastName || ''}`.trim() || 'N/A',
              // Use real API data for session findings (same as AppointmentDetails)
              sessionFindings: {
                selfHarmSymptoms: formatSymptomLevel(session.selfHarmSymptoms),
                depressionSymptoms: formatSymptomLevel(session.depressionSymptoms),
                anxietySymptoms: formatSymptomLevel(session.anxietySymptoms),
                angerIssues: formatSymptomLevel(session.angerIssues)
              },
              // Use real API data for recommendations (same as AppointmentDetails)
              recommendations: {
                assessments: prescriptionsByType.ASSESSMENT ? {
                  name: prescriptionsByType.ASSESSMENT.fileName || "Assessment",
                  url: prescriptionsByType.ASSESSMENT.url
                } : null,
                exercises: prescriptionsByType.EXERCISE ? {
                  name: prescriptionsByType.EXERCISE.fileName || "Exercise",
                  url: prescriptionsByType.EXERCISE.url
                } : null,
                clinicalConsultation: session.clinicalConsultation ? "Yes" : "No",
                medicalConsultation: session.medicalConsultation ? "Yes" : "No"
              }
            };
          })
          // Sort by date (most recent first)
          .sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            return dateB.getTime() - dateA.getTime();
          });

          setSessionsData(processedSessions);
        } else {
          // Even without appointment data, we can show member info
          setSessionsData([]);
        }

        setIsLoading(false);
      }
    } else if (!detailLoading && !appointmentLoading) {
      // If both API calls are complete but no data, show error state
      setIsLoading(false);
    }

    // Set a timeout to prevent infinite loading
    const timeout = setTimeout(() => {
      if (isLoading) {
        setIsLoading(false);
        // If we don't have member data, create a fallback
        if (!memberData) {
          setMemberData({
            memberName: "Sample Member",
            memberEmail: "sample@example.com",
            memberPhone: "+91-1234567890",
            memberGender: "Male",
            memberDOB: "1990-01-01",
            memberGroups: "Sample Group",
            createdOn: "2024-01-01",
            location: "Sample Location",
            emergencyContactName: "Sample Contact",
            emergencyContactRelationship: "Family",
            emergencyContactNumber: "+91-9876543210",
            organisationName: "Sample Organization"
          });
        }

        // Only add sample session data if we have no API data at all
        if (sessionsData.length === 0 && !appointmentCounterData && !detail) {
          setSessionsData([
            {
              appointmentId: "SAMPLE001",
              date: "2024-01-10",
              practitioner: "Dr. Sample Practitioner",
              status: "COMPLETED",
              mode: "Video Call",
              riskStatus: "LOW_RISK",
              prescriptions: [],
              sessionFindings: {
                selfHarmSymptoms: "None",
                depressionSymptoms: "Low",
                anxietySymptoms: "Moderate",
                angerIssues: "Low"
              },
              recommendations: {
                assessments: null,
                exercises: null,
                clinicalConsultation: "Yes",
                medicalConsultation: "No"
              }
            }
          ]);
        }
      }
    }, 5000);

    return () => clearTimeout(timeout);
  }, [appointmentCounterData, detail, params, isLoading, memberData, dispatch, detailLoading, appointmentLoading]);

  useEffect(() => {
    // Set document title for the PDF
    if (memberData && memberData.memberName !== "Sample Member") {
      const memberName = memberData.memberName || "Member";
      document.title = `Member Summary Report - ${memberName}`;
    }

    return () => {
      // Reset title when leaving the page, but only if we're not in a separate window
      if (window.opener === null) {
        document.title = "PhyberCare Admin";
      }
    };
  }, [memberData]);

  const formatDate = () => {
    return new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Show loading if data is not ready
  if (isLoading || !memberData) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-lg font-semibold text-[#2563EB] font-poppins mb-2">Preparing Report...</div>
          <div className="text-sm text-[#64748B] font-poppins">Please wait while we generate your PDF report</div>
        </div>
      </div>
    );
  }

  // Calculate overview stats from sessions data
  const overviewStats = {
    recentRiskStatus: sessionsData.length > 0 ? sessionsData[0].riskStatus : null,
    totalAppointments: sessionsData.length,
    completed: sessionsData.filter(s => s.status === "COMPLETED").length,
    followUps: 0,
    prescriptionsIssued: sessionsData.filter(s => s.prescriptions && s.prescriptions.length > 0).length
  };

  return (
    <>
      <div className="min-h-screen bg-white">
        {/* PDF Download Button */}
        <PDFDownloadButton memberData={memberData} />

        {/* Fixed Header and Footer for PDF pages (hidden on screen) */}
        <div className="pdf-header-running hidden">
          <PDFHeader />
        </div>
        <div className="pdf-footer-running hidden">
          <PDFFooter />
        </div>

        {/* PDF Content Container */}
        <div className="pdf-content max-w-[1728px] mx-auto p-8 font-poppins text-[#171717]">
        
        {/* Header - visible on screen, hidden in print */}
        <div className="print:hidden">
          <PDFHeader />
        </div>
        
        {/* Logo Section */}
        <LogoSection organisationLogo={memberData.organisationLogo || detail?.payload?.organisationLogo || detail?.data?.organisationLogo || detail?.organisationLogo} />

        {/* Document Header */}
        <div className="flex flex-row justify-between items-center p-0 gap-[582px]">
          <div className="font-poppins font-bold relative shrink-0 text-[48px]">
            <p className="leading-[normal] text-nowrap whitespace-pre">Member Summary Report</p>
          </div>
          <div className="font-poppins font-semibold relative shrink-0 text-[0px]">
            <p className="leading-[normal] not-italic text-[20px] text-nowrap whitespace-pre">
              <span className="font-poppins font-semibold">Report Date: </span>
              <span className="font-poppins font-normal">{formatDate()}</span>
            </p>
          </div>
        </div>

        {/* Basic Information Section */}
        <div className="bg-white box-border content-stretch flex items-start justify-between p-[16px] relative rounded-[16px] mb-12">
          <div aria-hidden="true" className="absolute border-[1px] border-[#E2E8F0] gap-y-4 border-solid inset-0 pointer-events-none rounded-[16px]" />
          
          {/* Basic Information */}
          <div className="content-stretch flex flex-col gap-y-[16px] items-start justify-start relative shrink-0">
            <div className="flex flex-col font-poppins font-semibold justify-center leading-[0] not-italic font-semibold relative shrink-0 text-[#2563EB] text-[16px] text-nowrap tracking-[0.32px]">
              <p className="leading-[24px] whitespace-pre">Basic Information</p>
            </div>
            <div className="content-stretch flex items-start justify-between leading-[0] not-italic relative shrink-0 text-[16px] text-nowrap w-[460px]">
              <div className="font-poppins font-normal relative shrink-0 text-[#334155]">
                <p className="leading-[24px] text-nowrap whitespace-pre">Member Name:</p>
              </div>
              <div className="font-poppins font-medium relative shrink-0 text-[#171717]">
                <p className="leading-[normal] text-nowrap whitespace-pre">{memberData.memberName}</p>
              </div>
            </div>
            <div className="content-stretch flex items-start justify-between leading-[0] not-italic relative shrink-0 text-[16px] text-nowrap w-[460px]">
              <div className="font-poppins font-normal relative shrink-0 text-[#334155]">
                <p className="leading-[24px] text-nowrap whitespace-pre">Contact Number:</p>
              </div>
              <div className="font-poppins font-medium relative shrink-0 text-[#171717]">
                <p className="leading-[normal] text-nowrap whitespace-pre">{memberData.memberPhone}</p>
              </div>
            </div>
            <div className="content-stretch flex items-start justify-between leading-[0] not-italic relative shrink-0 text-[16px] text-nowrap w-[460px]">
              <div className="font-poppins font-normal relative shrink-0 text-[#334155]">
                <p className="leading-[24px] text-nowrap whitespace-pre">Email Address:</p>
              </div>
              <div className="font-poppins font-medium relative font-medium shrink-0 text-[#171717]">
                <p className="leading-[normal] text-nowrap whitespace-pre">{memberData.memberEmail}</p>
              </div>
            </div>
            <div className="content-stretch flex items-start justify-between leading-[0] not-italic relative shrink-0 text-[16px] text-nowrap w-[460px]">
              <div className="font-poppins font-normal relative shrink-0 text-[#334155]">
                <p className="leading-[24px] text-nowrap whitespace-pre">Joined On:</p>
              </div>
              <div className="font-poppins font-medium relative font-medium shrink-0 text-[#171717]">
                <p className="leading-[normal] text-nowrap whitespace-pre">{formatApiDate(memberData.createdOn)}</p>
              </div>
            </div>
            <div className="content-stretch flex items-start justify-between leading-[0] not-italic relative shrink-0 text-[16px] text-nowrap w-[460px]">
              <div className="font-poppins font-normal relative shrink-0 text-[#334155]">
                <p className="leading-[24px] text-nowrap whitespace-pre">Gender:</p>
              </div>
              <div className="font-poppins font-medium relative shrink-0 text-[#171717]">
                <p className="leading-[normal] text-nowrap whitespace-pre">{memberData.memberGender}</p>
              </div>
            </div>
            <div className="content-stretch flex items-start justify-between leading-[0] not-italic relative shrink-0 text-[16px] text-nowrap w-[460px]">
              <div className="font-poppins font-normal relative shrink-0 text-[#334155]">
                <p className="leading-[24px] text-nowrap whitespace-pre">Age / DOB:</p>
              </div>
              <div className="font-poppins font-medium relative shrink-0 text-[#171717]">
                <p className="leading-[normal] text-nowrap whitespace-pre">{calculateAge(memberData.memberDOB)}</p>
              </div>
            </div>
          </div>

          {/* Vertical Separator */}
          <div className="w-px bg-[#E2E8F0] self-stretch mx-4" style={{height: 'auto'}}></div>

          {/* Organizational Information */}
          <div className="content-stretch flex flex-col gap-y-[16px] items-start justify-start relative shrink-0">
            <div className="flex flex-col font-poppins font-semibold justify-center leading-[0] font-semibold not-italic relative shrink-0 text-[#2563EB] text-[16px] text-nowrap tracking-[0.32px]">
              <p className="leading-[24px] whitespace-pre">Organizational Information</p>
            </div>
            <div className="content-stretch flex items-start justify-between leading-[0] not-italic relative shrink-0 text-[16px] text-nowrap w-[460px]">
              <div className="font-poppins font-normal relative shrink-0 text-[#334155]">
                <p className="leading-[24px] text-nowrap whitespace-pre">Organization:</p>
              </div>
              <div className="font-poppins font-medium relative font-medium shrink-0 text-[#171717] text-right">
                <p className="leading-[normal] text-nowrap whitespace-pre">{memberData.organisationName}</p>
              </div>
            </div>
            <div className="content-stretch flex items-start justify-between leading-[0] not-italic relative shrink-0 text-[16px] text-nowrap w-[460px]">
              <div className="font-poppins font-normal relative shrink-0 text-[#334155]">
                <p className="leading-[24px] text-nowrap whitespace-pre">Groups:</p>
              </div>
              <div className="font-poppins font-medium relative font-medium shrink-0 text-[#171717]">
                <p className="leading-[normal] text-nowrap whitespace-pre">{memberData.memberGroups}</p>
              </div>
            </div>
            <div className="content-stretch flex items-start justify-between leading-[0] not-italic relative shrink-0 text-[16px] text-nowrap w-[460px]">
              <div className="font-poppins font-normal relative shrink-0 text-[#334155]">
                <p className="leading-[24px] text-nowrap whitespace-pre">Location:</p>
              </div>
              <div className="font-poppins font-medium relative font-medium shrink-0 text-[#171717]">
                <p className="leading-[normal] text-nowrap whitespace-pre">{memberData.location}</p>
              </div>
            </div>
          </div>
          
          {/* Vertical Separator */}
          <div className="w-px bg-[#E2E8F0] self-stretch mx-4" style={{height: 'auto'}}></div>

          {/* Emergency Contact Details */}
          <div className="content-stretch flex flex-col gap-y-[16px] items-start justify-start relative shrink-0">
            <div className="flex flex-col font-poppins font-semibold justify-center leading-[0] font-semibold not-italic relative shrink-0 text-[#2563EB] text-[16px] text-nowrap tracking-[0.32px]">
              <p className="leading-[24px] whitespace-pre">Emergency Contact Details</p>
            </div>
            <div className="content-stretch flex items-start justify-between leading-[0] not-italic relative shrink-0 text-[16px] text-nowrap w-[460px]">
              <div className="font-poppins font-normal relative shrink-0 text-[#334155]">
                <p className="leading-[24px] text-nowrap whitespace-pre">Name</p>
              </div>
              <div className="font-poppins font-medium relative font-medium shrink-0 text-[#171717]">
                <p className="leading-[normal] text-nowrap whitespace-pre">{memberData.emergencyContactName}</p>
              </div>
            </div>
            <div className="content-stretch flex items-start justify-between leading-[0] not-italic relative shrink-0 text-[16px] text-nowrap w-[460px]">
              <div className="font-poppins font-normal relative shrink-0 text-[#334155]">
                <p className="leading-[24px] text-nowrap whitespace-pre">Relationship</p>
              </div>
              <div className="font-poppins font-medium relative font-medium shrink-0 text-[#171717] text-right">
                <p className="leading-[normal] text-nowrap whitespace-pre">{memberData.emergencyContactRelationship}</p>
              </div>
            </div>
            <div className="content-stretch flex items-start justify-between leading-[0] not-italic relative shrink-0 text-[16px] text-nowrap w-[460px]">
              <div className="font-poppins font-normal relative shrink-0 text-[#334155]">
                <p className="leading-[24px] text-nowrap whitespace-pre">Contact Number</p>
              </div>
              <div className="font-poppins font-medium relative font-medium shrink-0 text-[#171717] text-right">
                <p className="leading-[normal] text-nowrap whitespace-pre">{memberData.emergencyContactNumber}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Overview Stats Section */}
        <div className="overview-stats mb-12">
          <h2 className="font-poppins font-semibold text-[#171717] text-[24px] uppercase tracking-[0.48px] mb-3">
            Overview Stats
          </h2>
          <div className="bg-white border-[1px] border-solid border-[#E2E8F0] rounded-[16px] shadow-sm p-4">
            <div className="flex gap-6 justify-between">
              <div className="flex flex-col gap-4 flex-1">
                <div className="flex justify-between items-center w-[460px]">
                  <span className="font-poppins font-normal text-[#334155] text-[16px] ">Recent Risk Status</span>
                  <RiskStatusBadge risk={overviewStats.recentRiskStatus} session={sessionsData.length > 0 ? sessionsData[0] : null} />
                </div>
                <div className="flex justify-between w-[460px]">
                  <span className="font-poppins font-normal text-[#334155] text-[16px]">Total Appointments</span>
                  <span className="font-poppins font-medium text-[#171717] text-[16px] font-medium">{overviewStats.totalAppointments}</span>
                </div>
              </div>
              
              <div className="w-px bg-[#E2E8F0] self-stretch" style={{height: '65px'}}></div>
              
              <div className="flex flex-col gap-4 flex-1">
                <div className="flex justify-between w-[460px]">
                  <span className="font-poppins font-normal text-[#334155] text-[16px]">Completed</span>
                  <span className="font-poppins font-medium text-[#171717] text-[16px] font-medium">{overviewStats.completed}</span>
                </div>
                <div className="flex justify-between w-[460px]">
                  <span className="font-poppins font-normal text-[#334155] text-[16px]">Follow-ups</span>
                  <span className="font-poppins font-medium text-[#171717] text-[16px] font-medium">{overviewStats.followUps}</span>
                </div>
              </div>
              
              <div className="w-px bg-[#E2E8F0] self-stretch" style={{height: '65px'}}></div>
              
              <div className="flex justify-between  flex-1 w-[460px]">
                <span className="font-poppins font-normal text-[#334155] text-[16px]">Prescriptions Issued</span>
                <span className="font-poppins font-medium text-[#171717] text-[16px] font-medium">{overviewStats.prescriptionsIssued}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Separator */}
        <div className="flex justify-center mb-12">
          <div className="h-px bg-[#E2E8F0] w-[350px]"></div>
        </div>

        {/* Session Details Section */}
        <div className="sessions-section mb-12">
          {/* Session Header */}
          <div className="bg-[#F8FAFC] border-[1px] border-solid border-[#E2E8F0] rounded-xl px-3 py-1 mb-6 flex justify-between items-center">
            <h2 className="font-poppins font-semibold text-[#171717] font-semibold text-[24px] uppercase">
              Session Details
            </h2>
            <div className="font-poppins font-semibold text-[20px] font-semibold">
              <span>Total Sessions: </span>
              <span className="font-medium">{sessionsData.length}</span>
            </div>
          </div>

          {/* Sessions */}
          <div className="flex flex-col gap-3">
            {sessionsData.map((session, index) => {
              // Calculate chronological session number (reverse of array index + 1)
              const sessionNumber = sessionsData.length - index;
              
              return (
                <div key={session.appointmentId || index} className="session-card">
                  {/* Session Summary Card */}
                  <div className="bg-white border-[1px] border-solid border-[#E2E8F0] rounded-[16px] shadow-sm p-4 mb-3">
                    <div className="flex gap-8">
                      {/* Session Info */}
                      <div className="flex flex-col gap-4 flex-1">
                        <div className="flex justify-between">
                          <span className="font-poppins font-normal text-[#334155] text-[16px]">Consultation</span>
                          <span className="font-poppins font-semibold text-[#2563EB] text-[18px] font-semibold">{sessionNumber}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-poppins font-normal text-[#334155] text-[16px]">Appointment ID</span>
                          <span className="font-poppins font-semibold text-[#2563EB] text-[18px] font-semibold">{session.appointmentId}</span>
                        </div>
                      </div>
                      
                      <div className="w-px bg-[#E2E8F0] self-stretch" style={{height: '97px'}}></div>
                      
                      {/* Date and Practitioner */}
                      <div className="flex flex-col gap-4 flex-1">
                        <div className="flex justify-between">
                          <span className="font-poppins font-normal text-[#334155] text-[16px]">Date</span>
                          <span className="font-poppins font-medium text-[#171717] text-[16px] font-medium">{formatApiDate(session.date)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-poppins font-normal text-[#334155] text-[16px]">Practitioner Name</span>
                          <span className="font-poppins font-medium text-[#171717] text-[16px] font-medium">{session.practitioner}</span>
                        </div>
                      </div>
                      
                      <div className="w-px bg-[#E2E8F0] self-stretch" style={{height: '97px'}}></div>
                      
                      {/* Status and Risk */}
                      <div className="flex flex-col gap-3 flex-1">
                        <div className="flex justify-between">
                          <span className="font-poppins font-normal text-[#334155] text-[16px]">Status</span>
                          <StatusIndicator session={session} />
                        </div>
                        <div className="flex justify-between">
                          <span className="font-poppins font-normal text-[#334155] text-[16px]">Mode</span>
                          <span className="font-poppins font-medium text-[#171717] text-[16px] font-medium">{session.mode}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="font-poppins font-normal text-[#334155] text-[16px]">Overall Risk</span>
                          <RiskStatusBadge risk={session.riskStatus || "NO_RISK"} session={session} />
                        </div>
                      </div>
                      
                      <div className="w-px bg-[#E2E8F0] self-stretch" style={{height: '97px'}}></div>
                      
                      {/* Prescriptions and Notes */}
                      <div className="flex flex-col gap-3 flex-1">
                        <div className="flex justify-between">
                          <span className="font-poppins font-normal text-[#334155] text-[16px]">Prescriptions</span>
                          <span className="font-poppins font-medium text-[#171717] text-[16px] font-medium">
                            {session.prescriptions && session.prescriptions.length > 0 ? "Added" : "N/A"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-poppins font-normal text-[#334155] text-[16px]">Notes</span>
                          <span className="font-poppins font-medium text-[#171717] font-medium text-[16px]">
                            {session.notes ? "Added" : "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Session Findings Card */}
                  <div className="bg-white border-[1px] border-solid border-[#E2E8F0] rounded-[16px] shadow-sm p-4 mb-3">
                    <div className="flex flex-col gap-2">
                      <h4 className="font-poppins font-semibold text-[#2563EB] text-[18px] underline">
                        Session Findings
                      </h4>
                      <div className="flex flex-col gap-2">
                        {Object.entries(session.sessionFindings || {}).map(([key, value]) => (
                          <div key={key} className="flex justify-between">
                            <span className="font-poppins font-normal text-[#334155] text-[16px]">
                              {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                            </span>
                            <span className="font-poppins font-medium text-[#171717] font-medium text-[16px]">{value || 'N/A'}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Recommendations Card */}
                  <div className="bg-white border-[1px] border-solid border-[#E2E8F0] rounded-[16px] shadow-sm p-4">
                    <div className="flex flex-col gap-2">
                      <h4 className="font-poppins font-semibold text-[#2563EB] text-[18px] underline">
                        Recommendations
                      </h4>
                      <div className="flex flex-col gap-2">
                        <div className="flex justify-between">
                          <span className="font-poppins font-normal text-[#334155] text-[16px] font-medium">
                            Assessments
                          </span>
                          <span className="font-poppins font-normal text-[#171717] text-[16px]">
                            {session.recommendations?.assessments?.name || 'N/A'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-poppins font-normal text-[#334155] text-[16px] font-medium">
                            Exercises
                          </span>
                          <span className="font-poppins font-normal text-[#171717] text-[16px]">
                            {session.recommendations?.exercises?.name || 'N/A'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-poppins font-normal text-[#334155] text-[16px] font-medium">
                            Clinical Consultation
                          </span>
                          <span className="font-poppins font-normal text-[#171717] text-[16px]">
                            {session.recommendations?.clinicalConsultation || 'N/A'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-poppins font-normal text-[#334155] text-[16px] font-medium">
                            Medical Consultation
                          </span>
                          <span className="font-poppins font-normal text-[#171717] text-[16px]">
                            {session.recommendations?.medicalConsultation || 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Separator between sessions */}
                  {index < sessionsData.length - 1 && (
                    <div className="flex justify-center my-6">
                      <div className="h-0.5 bg-[#E2E8F0] w-full"></div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer - visible on screen, hidden in print */}
        <div className="print:hidden">
          <PDFFooter />
        </div>
      </div>
      </div>

      {/* Print-specific CSS for proper PDF generation */}
      <style jsx>{`
        @media print {
          @page {
            size: A3;
            margin-top: 0.75in;
          }
          
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
            font-family: 'Poppins', sans-serif;
          }
          
          .pdf-content {
            max-width: none !important;
            margin: 0 !important;
            padding: 80px 0 100px 0 !important;
          }
          
          .pdf-header-running {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            height: 80px;
            z-index: 1000;
            background: white;
            display: block !important;
          }
          
          .pdf-footer-running {
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            height: 100px;
            z-index: 1000;
            background: white;
            display: block !important;
          }
          
          .print\\:hidden {
            display: none !important;
          }
          
          .session-card {
            break-inside: avoid;
            page-break-inside: avoid;
            margin-bottom: 20px;
          }
          
          .overview-stats {
            break-inside: avoid;
            page-break-inside: avoid;
          }
          
          * {
            -webkit-print-color-adjust: exact;
            color-adjust: exact;
          }
          
          .bg-\\[\\#0b4f9f\\] {
            background-color: #0b4f9f !important;
          }
        }
      `}</style>
    </>
  );
}